import { observer } from 'mobx-react-lite';
import { useLoaderData, useNavigate } from 'react-router';
import View from '@components/view/view.tsx';
import experienceTagStore from '@store/experience-tag-store.ts';
import { IconEnum } from '@components/core/icon/icon-enum.ts';
import { ExperienceTagDetailsLoaderReturn } from '@views/experience-tag-details/experience-tag-details-loader.ts';
import ExperienceStats from '@components/data/experience-stats/experience-stats.tsx';
import { ExperienceStatType } from '@/enums/experience-stat-type.ts';
import ExperienceGenreDistribution from '@components/data/experience-genre-distribution/experience-genre-distribution.tsx';
import ViewPanel from '@components/view/view-panel.tsx';
import ViewPanelSizeEnum from '@components/view/view-panel-size-enum.ts';
import LightdashDashboards from '@components/data/lightdash-dashboards/lightdash-dashboards.tsx';
import DashboardType from '@components/data/lightdash-dashboards/dashboard-type.ts';
import toastStore from '@store/toast-store.ts';
import { ToastType } from '@components/service/toast/toast-enums.ts';
import { useEffect } from 'react';
import postHogService from '@services/posthog-service.ts';
import { PostHogEventType } from '@/enums/posthog-event.ts';
import ViewExpansionPanel from '@components/view/view-expansion-panel.tsx';
import modalStore from '@store/modal-store.ts';
import ExpansionButton from '@components/core/expansion-button/expansion-button.tsx';
import Icon from '@components/core/icon/icon.tsx';
import ExperienceTagDetailsCcu from '@views/experience-tag-details/view-components/experience-tag-details-ccu.tsx';
import authStore from '@store/auth-store.ts';
import { UserPermission } from '@/schemas/user-schema.ts';
import ClassString from '@utils/class-string.ts';
import ExperienceTagDetailsBulkAdd from '@views/experience-tag-details/view-components/experience-tag-details-bulk-add.tsx';
import Tooltip from '@components/core/tooltip/tooltip.tsx';
import copyToClipboard from '@utils/copy-to-clipboard.ts';
import { DataGranularity } from '@/enums/data-granularity.ts';
import ModalSignUpCta from '@components/modal/modal-sign-up-cta.tsx';
import AverageTagPlaytime from '@components/data/average-tag-playtime/average-tag-playtime.tsx';
import ExperienceTagDetailsAddButton from '@views/experience-tag-details/view-components/experience-tag-details-add-button.tsx';
import ExperienceTagDetailsMainStats from '@views/experience-tag-details/view-components/experience-tag-details-main-stats.tsx';
import ExperienceTagDetailsAverages from '@views/experience-tag-details/view-components/experience-tag-details-averages.tsx';
import localStorageService from '@services/local-storage-service.ts';
import { LocalStorageKey } from '@/enums/local-storage-types.ts';
import ExperienceTagDetailsExperiences from '@views/experience-tag-details/view-components/experience-tag-details-experiences.tsx';

const ExperienceTagDetails = observer(function ExperienceTagDetails() {
	const { populatedTag } = useLoaderData<ExperienceTagDetailsLoaderReturn>();

	const tag =
		experienceTagStore.populatedExperienceTag(populatedTag.id) ??
		populatedTag;

	const canAddExperiences =
		authStore.havePermission(UserPermission.EDIT_TAG) ||
		tag.owner_id === authStore.user?.id;

	const loggedIn = authStore.isLoggedIn;

	const navigate = useNavigate();

	const top5Experiences = [...tag.places]
		.sort((a, b) => b.players_online - a.players_online)
		.slice(0, 5);

	const startExpanded = () => {
		if (!tag.places.length) {
			return true;
		}

		const user = authStore.user;
		if (!user) {
			return true;
		}

		return (
			localStorageService.get(
				LocalStorageKey.TagDetailsExperienceListState,
				user.id
			) !== 'false'
		);
	};

	const setListState = (state: boolean) => {
		const user = authStore.user;
		if (!user) {
			return;
		}

		localStorageService.set(
			LocalStorageKey.TagDetailsExperienceListState,
			`${state}`,
			user.id
		);
	};

	useEffect(() => {
		postHogService.capture({
			type: PostHogEventType.ViewTag,
			tag: tag.name,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<View
			viewInfo={{
				title: tag?.name ?? '',
				icon: IconEnum.TAG,
				breadCrumbs: [{ title: 'Tags', route: '/tags' }],
				metaTagDescription: `Track real-time stats for ${tag.name} games. View average CCU, average playtime, and more.`,
			}}
			headerChildren={
				tag && (
					<div
						className={
							'w-full flex items-center justify-between flex-wrap'
						}
					>
						<div
							className={
								'flex justify-center items-center gap-2 text-sm'
							}
						>
							{tag.global && (
								<Tooltip
									text={'Curated'}
									direction={'right'}
									widthClass={'w-fit'}
								>
									<Icon
										icon={IconEnum.CURATED}
										size={'2rem'}
									/>
								</Tooltip>
							)}
						</div>

						<div
							className={
								'flex items-center justify-end flex-wrap'
							}
						>
							{canAddExperiences && (
								<ExperienceTagDetailsAddButton tag={tag} />
							)}

							<div
								className={ClassString({
									dynamic: {
										'border-l pl-4 dark:border-gray-400':
											canAddExperiences,
									},
								})}
							>
								<ExpansionButton
									title={'Tag Options'}
									subMenuButtons={[
										{
											title: (
												<div
													className={
														'flex justify-center items-center gap-2 px-4'
													}
												>
													{'Compare To Experience'}
													<Icon
														icon={
															loggedIn
																? IconEnum.COMPARE
																: IconEnum.LOCK
														}
														size={'1.5rem'}
													/>
												</div>
											),
											onClick: () => {
												if (loggedIn) {
													void navigate(
														`/compare/experience-tag#tag-1=${tag.id}`
													);
												} else {
													modalStore.open(
														<ModalSignUpCta />
													);
												}
											},
										},
										{
											title: (
												<div
													className={
														'flex justify-center items-center gap-2 px-4'
													}
												>
													{'Compare To Tag'}
													<Icon
														icon={
															loggedIn
																? IconEnum.COMPARE
																: IconEnum.LOCK
														}
														size={'1.5rem'}
													/>
												</div>
											),
											onClick: () => {
												if (loggedIn) {
													void navigate(
														`/compare/tags#tag-1=${tag.id}`
													);
												} else {
													modalStore.open(
														<ModalSignUpCta />
													);
												}
											},
										},
										...(canAddExperiences
											? [
													{
														title: (
															<div
																className={
																	'flex justify-center items-center gap-2 px-4'
																}
															>
																{'Bulk Add'}
																<Icon
																	icon={
																		IconEnum.BULK_ADD
																	}
																/>
															</div>
														),
														onClick: () => {
															modalStore.open(
																<ExperienceTagDetailsBulkAdd
																	tag={tag}
																/>
															);
														},
													},
												]
											: []),
										{
											title: (
												<div
													className={
														'flex justify-center items-center gap-2 px-4'
													}
												>
													{'Copy Experience IDs'}
													<Icon
														icon={IconEnum.COPY}
														size={'1.5rem'}
													/>
												</div>
											),
											onClick: () => {
												copyToClipboard(
													tag.places
														.map(
															(exp) =>
																exp.place_id
														)
														.join(', ')
												);
												toastStore.emit(
													'Experience IDs has been copied to your clipboard.',
													ToastType.INFO
												);
											},
										},
									]}
								/>
							</div>
						</div>
					</div>
				)
			}
			className={'experience-tag-details'}
		>
			<ExperienceTagDetailsMainStats tag={tag} />

			<ViewExpansionPanel
				title={`Experiences (${tag.places.length})`}
				startExpanded={startExpanded()}
				onStateChange={setListState}
			>
				<ExperienceTagDetailsExperiences tag={tag} />
			</ViewExpansionPanel>

			{!!tag.places.length && (
				<>
					<ExperienceTagDetailsCcu tag={tag} />

					<ViewPanel>
						<AverageTagPlaytime tag={tag} />
					</ViewPanel>

					<ViewPanel options={{ size: ViewPanelSizeEnum.HALF }}>
						<ExperienceGenreDistribution
							title={'Tag Genre Distribution'}
							experiences={[...tag.places]}
							height={400}
						/>
					</ViewPanel>

					<ExperienceTagDetailsAverages tag={tag} />
				</>
			)}

			{!!top5Experiences.length && (
				<ViewPanel>
					<ExperienceStats
						experiences={top5Experiences}
						statType={ExperienceStatType.CCUS}
						title={'Concurrent Users (Current Top 5)'}
						options={{
							showLegend: true,
							useLineGraph: true,
							thickerLines: true,
							selectedRange: 7,
							defaultGranularity: DataGranularity.WEEK,
							sharedTooltip: true,
						}}
					/>
				</ViewPanel>
			)}

			<LightdashDashboards
				dashboards={[
					{
						type: DashboardType.EXPERIENCE_INSIGHT_BY_TAG,
						tagName: tag?.name,
					},
				]}
			/>
		</View>
	);
});

export default ExperienceTagDetails;
